/**
 * @file xeroArchiveNotice.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-22
 * @modified 2026-11-02
 * @description Archiving something here does not archive it in Xero — say so,
 *              until it stops being true.
 *
 *              WHY THE RECIPROCAL IS NEEDED TODAY. Xero's Item API cannot
 *              report archived state and will not accept a change to it: the
 *              flag is ours alone, seeded from a CSV export (see CLAUDE.md). So
 *              archiving an item here leaves it live in Xero, where it keeps
 *              appearing in pickers for anybody still ordering there — and
 *              restoring it here leaves a product hidden in Xero that this
 *              system thinks is available.
 *
 *              WHY IT STOPS. Once ordering runs from this system alone, what is
 *              active HERE is what matters, and telling somebody to go and
 *              repeat the change in a system nobody orders from is busywork
 *              dressed as diligence. The cutover date is below; after it the
 *              wording goes back to the plain question with no second step.
 *
 *              A DATE, NOT A FEATURE FLAG, deliberately: it needs no deploy on
 *              the day and no one has to remember. The cost is that it changes
 *              behaviour by itself, which is why it is one constant with its
 *              reasoning attached rather than a date buried in a template.
 */

/**
 * The day the reciprocal Xero step stops being asked for — the point from which
 * customer orders are placed only in this system.
 *
 * Local midnight on purpose: the office is in one place, and "the 1st" means
 * the 1st there rather than whenever UTC catches up.
 */
export const XERO_ARCHIVE_CUTOVER = new Date(2026, 9, 1); // 1 October 2026

/** Is the reciprocal Xero change still needed? */
export const xeroArchiveStepNeeded = (now = new Date()) => now < XERO_ARCHIVE_CUTOVER;

const CUTOVER_LABEL = XERO_ARCHIVE_CUTOVER.toLocaleDateString('en-GB', {
  day: 'numeric', month: 'long', year: 'numeric'
});

/**
 * The confirm text for archiving or restoring something Xero also holds.
 *
 * @param {'archive'|'restore'} action
 * @param {string} label       What is being changed, e.g. "FEED01 - Layers Pellets".
 * @param {string} [noun]      What kind of thing it is, for the wording.
 * @param {Date}   [now]       Injectable, so the behaviour is testable.
 */
export function xeroArchiveConfirmMessage(action, label, noun = 'item', now = new Date()) {
  const plain = `Are you sure you want to ${action} "${label}"?`;
  if (!xeroArchiveStepNeeded(now)) return plain;

  const there = action === 'archive'
    ? `This only ${noun === 'item' ? 'archives it' : 'archives them'} here. Xero cannot be told to archive an ${noun} through its API, so it stays live in Xero and will keep appearing for anyone ordering there.`
    : `This only restores it here. Xero cannot be told about the change through its API, so if it is archived in Xero it will stay hidden there.`;

  return `${plain}\n\n${there}\n\nPlease make the same change in Xero. This reminder stops on ${CUTOVER_LABEL}, when ordering moves to this system alone.`;
}

/**
 * Restoring an item here never restores it in Xero (Cam, 2 Nov 2026 — "Allow
 * users to restore archived items please with a reminder that it will stay
 * archived in Xero and give a URL to Xero they can copy/ paste if they want
 * (not a link)"). Xero's Item API can neither report nor change archived
 * state, so the reminder is permanent, not tied to the cutover above.
 *
 * The item's page in Xero, as text to copy — or null for an item Xero has
 * never held (no id, or a LOCAL- / TEMP- placeholder).
 */
export function xeroItemUrl(xeroItemId) {
  const id = String(xeroItemId || '').trim();
  if (!id || /^(LOCAL|TEMP)-/i.test(id)) return null;
  return `https://go.xero.com/Accounts/Inventory/${encodeURIComponent(id)}`;
}

/** The words of the restore reminder, before and after. */
export function xeroItemRestoreWords(xeroItemId) {
  const url = xeroItemUrl(xeroItemId);
  if (!url) {
    return { url: null, before: 'It isn’t in Xero, so nothing needs doing there.', after: '' };
  }
  return {
    url,
    before: 'It will stay archived in Xero: Xero can’t be told through its API to restore an item. To restore it there as well, copy this address into your browser:',
    after: `It is still archived in Xero. To restore it there, copy this address into your browser: ${url}`
  };
}
